import { useState } from 'react';
import { api } from '../../../lib/api';
import { CreateVideoDto, Video } from '../../../types/videos';

export function useCreateVideo(onSuccess?: (id: string) => void) {
  const [form, setForm] = useState<CreateVideoDto>({ filename: '', sourceFilename: '' });
  const [durationSec, setDurationSec] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = <K extends keyof CreateVideoDto>(key: K, value: CreateVideoDto[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const uploadFile = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      const desired = (form.sourceFilename || file.name || '').toString();
      formData.append('filename', desired);
      formData.append('file', file);
      const resp = await api.post<{ message?: string; data?: { filename: string; durationSec?: number } }>(
        '/videos/upload-file',
        formData,
      );
      const payload = (resp as any).data || {};
      const filename = payload.filename || (resp as any).filename; // tolerate either shape
      if (!filename) throw new Error('Upload failed: no filename');
      setForm(prev => ({ ...prev, sourceFilename: filename }));
      setDurationSec(payload.durationSec);
      return filename;
    } catch (e: any) {
      setError(e.message || 'Upload failed');
      throw e;
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload: CreateVideoDto = {
        filename: form.filename,
        sourceFilename: form.sourceFilename,
      };
      const res = await api.post<{ data: Video }>("/videos", payload);
      onSuccess?.((res as any).data?.data?.id || (res as any).data?.id || (res as any).id);
    } catch (e: any) {
      setError(e.message || 'Failed to create');
    } finally {
      setLoading(false);
    }
  };

  return { form, setField, uploadFile, submit, loading, uploading, error, durationSec };
}
